import { useMemo } from 'react';

import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';

import { IDiscount, ILegacyCartEntry, IServerOrder } from '@rbi-ctg/menu';
import { DiscountTypes, PosVendor } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { IAppliedRewards } from 'state/loyalty/hooks/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { computeOtherDiscountAmount, getLoyaltyRewardsDiscount } from 'utils/cart/helper';

import { CheckoutTotals } from '../types';

import { useCheckoutContext } from './use-checkout-context';

type TipTotals = Omit<CheckoutTotals, 'grandTotal' | 'tipAmount'>;

type GetTotalsInput = {
  serverOrder: IServerOrder | null;
  cartEntries: ILegacyCartEntry[];
  isLoyaltyEnabled?: boolean;
  appliedLoyaltyRewards?: IAppliedRewards;
  isPricingRewardApplication?: boolean;
};
export const getTotals = ({
  serverOrder,
  cartEntries,
  isLoyaltyEnabled,
  isPricingRewardApplication,
  appliedLoyaltyRewards,
}: GetTotalsInput): TipTotals => {
  const { cart, delivery } = serverOrder ?? {};
  const deliveryFee = delivery ? delivery.feeCents : null;
  const deliveryFeeDiscount = delivery ? delivery.feeDiscountCents : null;
  const allDiscounts = cart?.discounts ?? [];
  const posRewardDiscount = () => {
    return allDiscounts.find(
      (discount: IDiscount) => discount.name === DiscountTypes.REWARDS_DISCOUNTS
    )?.value;
  };
  const otherDiscountAmount = computeOtherDiscountAmount(allDiscounts);

  // delivery.tipCents is null until the user advances to PAYMENT_REQUESTED
  // at the same time, totalCents increases by the tip amount
  // we needed to do this to render the correct grand total/tip as user advances
  const serverOrderTip = Number(delivery?.tipCents ?? 0);

  // currently not all POS vendors return discount amount in cart response when offers are applied
  // for loyalty rewards with loyaltyEnabled = true, the discount amount is calculated in the FE
  // otherwise it is returned from the POS system
  const rewardDiscountAmount =
    isLoyaltyEnabled && !isPricingRewardApplication
      ? getLoyaltyRewardsDiscount({
          cartEntries,
          loyaltyEnabled: isLoyaltyEnabled,
          appliedLoyaltyRewards,
        })
      : posRewardDiscount();

  let additionalDiscountAmount = otherDiscountAmount;

  // subtract loyalty reward amount for posVendors that return "otherDiscountAmount" e.g. carrols
  // Carrols was determined to be the only POS vendor requiring this logic
  if (
    isLoyaltyEnabled &&
    rewardDiscountAmount &&
    additionalDiscountAmount &&
    additionalDiscountAmount >= rewardDiscountAmount &&
    serverOrder?.cart.posVendor === PosVendor.CARROLS
  ) {
    additionalDiscountAmount -= rewardDiscountAmount;
  }

  const { subTotalCents = 0, taxCents = 0 } = cart || {};

  return {
    subTotalCents,
    taxCents,
    rewardDiscountAmount,
    serverOrderTip,
    additionalDiscountAmount,
    deliveryFee,
    deliveryFeeDiscount,
  };
};

export const useCheckoutTotals = () => {
  const { cartEntries } = useCheckoutContext();
  const isLoyaltyEnabled = useIsLoyaltyEnabled();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const isPricingRewardApplication = useAppSelector(
    selectors.loyalty.selectIsPricingRewardApplication
  );
  const { serverOrder, isDeliveryOrder, cart, tipAmount } = useCheckoutContext();
  const deliveryTipCents = Number(serverOrder?.delivery?.tipCents ?? 0);
  const totals = getTotals({
    serverOrder,
    cartEntries,
    isLoyaltyEnabled,
    appliedLoyaltyRewards,
    isPricingRewardApplication,
  });
  const { cartTotalCents } = useCartWithServerOrderSurcharges(cart);

  const grandTotal = useMemo(() => {
    return Math.max(cartTotalCents - deliveryTipCents + (isDeliveryOrder ? tipAmount : 0), 0);
  }, [cartTotalCents, deliveryTipCents, isDeliveryOrder, tipAmount]);

  return { ...totals, tipAmount, grandTotal } as CheckoutTotals;
};
